import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent } from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import { Copy, Check, User, Shield, User2Icon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { usePermissionManagement } from "../../Services/PermissionManagement";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { toast } from "react-toastify";
import { useUserContext } from "../../../Contexts/UserProvider";
import { isApiError } from "../../../Utilities/apiResponse";

const UserDetailsSheet = ({ user, open, onOpenChange, allRoles = [], initialTab = "details" }) => {
  const { assignUserByRole } = usePermissionManagement();
  const { tenant } = useUserContext();
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  useEffect(() => {
    setSelectedRole(user?.roleId || null);
  }, [user]);

  const copyToClipboard = (text: string, field: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRoleName = (roleId: string) => {
    return allRoles?.find((item: any) => item.id === roleId)?.roleName || "—";
  };

  const getUserAvatar = (email: string) => {
    const initials = email ? email.substring(0, 2).toUpperCase() : "??";
    return (
      <Avatar className="h-20 w-20 border">
        <AvatarImage src="/placeholder.svg" alt={email || "User"} />
        <AvatarFallback className="bg-purple-100 text-purple-800 text-xl">{initials}</AvatarFallback>
      </Avatar>
    );
  };

  const handleAssignRole = () => {
    assignUserByRole(selectedRole, user?.id)
      .then((response) => {
        if (isApiError(response)) {
          toast.error("Role assignment failed");
          return;
        }
        toast.success("Role assigned");
      })
      .catch(() => {
        toast.error("Role assignment failed");
      });
  };

  const email = user?.email || user?.userEmail;
  const displayName = user?.fullName || email || "Unknown User";
  const createdDate = user?.createdDate || user?.joinedDate;
  const status = user?.status || user?.notificationStatus || "Active";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto user-detail-view">
        {/* <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <User2Icon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold text-gray-900">User Details</SheetTitle>
                <p className="text-sm text-gray-600 mt-1">{user?.roleName || ""}</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="close-panel">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </Button>
        </SheetHeader> */}

        <SheetHeader className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <User2Icon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <SheetTitle className="text-xl font-bold">User Details</SheetTitle>
                  <p className="text-sm mt-1">View the details and permissions assigned to this user</p>
                </div>
              </div>
            </div>
          </SheetHeader>

        <Tabs defaultValue={initialTab} className="w-full">
          <div className="px-6 pt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">User Details</TabsTrigger>
              <TabsTrigger value="permission">Permission</TabsTrigger>
            </TabsList>
          </div>

          <div className="p-6">
            <ScrollArea className="h-[400px]">
              <TabsContent value="details" className="mt-0 space-y-6">
                <div className="flex items-center space-x-4">
                  {getUserAvatar(email)}
                  <div>
                    <h3 className="text-xl font-semibold">{displayName}</h3>
                    <Badge className="bg-green-100 text-green-800 mt-1">{user.roleName || ""}</Badge>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <TooltipProvider>
                      <div className="flex items-center">
                        <p className="font-medium truncate mr-2">{email || "—"}</p>
                        {email && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => copyToClipboard(email, "email")}
                              >
                                {copied === "email" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Copy email</p></TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TooltipProvider>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Role</p>
                    <p className="font-medium">{user?.roleName || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Organization</p>
                    <p className="font-medium">{tenant?.name || "—"}</p>
                  </div>
                </div>

                <Card className="bg-gray-50 border">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">User ID</p>
                        <div className="flex items-center">
                          <p className="font-mono text-sm truncate mr-2">{user?.id || "—"}</p>
                          {user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(user.id, "userId")}
                            >
                              {copied === "userId" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="permission" className="mt-0 space-y-6">
                <div>
                  <label className="text-xs font-bold">User Role</label>
                  <Select
                    value={selectedRole || undefined}
                    onValueChange={(value) => setSelectedRole(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select User Role" />
                    </SelectTrigger>
                    <SelectContent>
                      {allRoles?.map((item: any, index: number) => (
                        <SelectItem key={item?.id || index} value={item?.id}>
                          {item?.roleName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="border-t mt-4 flex justify-end gap-2">
                    <Button className="bg-primary hover:bg-[#5f50d8]" onClick={handleAssignRole}>
                      <Shield className="h-4 w-4 mr-2" />
                      Assign Role
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default UserDetailsSheet;
